import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ChevronRight, ArrowRight, Wrench, Sparkles, ShieldCheck, Cpu, Package, Truck, Settings } from "lucide-react";
import { api, resolveImg } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const ICONS = { Wrench, Sparkles, ShieldCheck, Cpu, Package, Truck, Settings };
const SITE = "https://simserv.ro";

export default function ServicesHub() {
  const [content, setContent] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/content").then((r) => setContent(r.data)).catch(() => {});
  }, []);

  if (!content)
    return <div className="min-h-screen flex items-center justify-center text-[#0F1E36]">Se încarcă...</div>;

  const { settings, services } = content;
  const company = settings.company;
  const slugOf = (s) => s.slug || s.id;

  return (
    <div className="bg-white">
      <Helmet>
        <title>Servicii Auto în Slobozia | SIMSERV Auto Service</title>
        <meta
          name="description"
          content="Toate serviciile SIMSERV Slobozia: mecanică, tinichigerie și vopsitorie, ITP autorizat RAR, diagnoză electronică și piese de schimb. Programări rapide."
        />
        <link rel="canonical" href={`${SITE}/servicii`} />
        <meta property="og:title" content="Servicii Auto în Slobozia | SIMSERV Auto Service" />
        <meta property="og:url" content={`${SITE}/servicii`} />
        <meta property="og:type" content="website" />
      </Helmet>
      <Navbar company={company} />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <nav className="flex items-center gap-1.5 text-sm text-slate-500 mb-6">
          <Link to="/" className="hover:text-[#FF5A1F]">Acasă</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <span className="text-[#0F1E36] font-medium">Servicii</span>
        </nav>

        <div className="max-w-2xl mb-12">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#E11D2A] mb-3">// Serviciile Noastre</p>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0F1E36]">
            Servicii auto complete în Slobozia
          </h1>
          <p className="mt-4 text-base text-slate-600 leading-relaxed">
            De la revizii și ITP până la tinichigerie, vopsitorie și diagnoză — alege un serviciu pentru detalii complete și programare rapidă.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s) => {
            const Icon = ICONS[s.icon] || Wrench;
            return (
              <Link
                key={s.id}
                to={`/servicii/${slugOf(s)}`}
                data-testid={`hub-service-${slugOf(s)}`}
                className="group relative bg-white rounded-2xl border border-[#E2E8F0] overflow-hidden hover:border-[#FF5A1F] hover:shadow-xl hover:shadow-slate-200/60 transition-all duration-300"
              >
                {s.image_url ? (
                  <div className="h-40 overflow-hidden">
                    <img src={resolveImg(s.image_url)} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                ) : null}
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <span className="w-12 h-12 rounded-xl bg-[#0F1E36] flex items-center justify-center group-hover:bg-[#FF5A1F] transition-colors duration-300">
                      <Icon className="w-6 h-6 text-white" />
                    </span>
                    {s.badge ? (
                      <span className="text-[11px] font-bold uppercase tracking-wide text-[#E11D2A] bg-[#E11D2A]/10 px-2.5 py-1 rounded-full">
                        {s.badge}
                      </span>
                    ) : null}
                  </div>
                  <h2 className="font-display text-xl font-bold text-[#0F1E36] leading-snug group-hover:text-[#FF5A1F] transition-colors duration-200">
                    {s.title}
                  </h2>
                  <p className="mt-2.5 text-sm text-slate-600 leading-relaxed line-clamp-3">{s.description}</p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#FF5A1F]">
                    Vezi detalii
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </main>
      <Footer company={company} />
    </div>
  );
}
